<template>
  <div class="tw-container tw-mx-auto tw-px-4 tw-py-8">
    <div v-if="!loading">
      <!-- Success Header -->
      <v-card class="tw-mb-8" color="success" dark elevation="8">
        <v-card-text class="tw-p-6 tw-text-center">
          <v-icon size="64" class="tw-mb-4">mdi-check-circle</v-icon>
          <h1 class="tw-text-3xl tw-font-bold tw-mb-2">Policy Issued Successfully!</h1>
          <p class="tw-text-lg tw-opacity-90">Your travel insurance policy has been issued and is ready for download.</p>
        </v-card-text>
      </v-card>

      <!-- Policy Details Card -->
      <v-card class="tw-mb-8" elevation="4">
        <v-card-title class="tw-bg-primary tw-text-white">
          <v-icon class="tw-mr-3">mdi-shield-check</v-icon>
          Policy Details
        </v-card-title>

        <v-card-text class="tw-p-6">
          <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-6">
            <!-- Policy Information -->
            <div>
              <h3 class="tw-text-lg tw-font-semibold tw-mb-4 tw-text-primary">Policy Information</h3>
              <div class="tw-space-y-3">
                <div class="tw-flex tw-justify-between tw-items-center">
                  <span class="tw-text-gray-600">Policy Number:</span>
                  <span class="tw-font-semibold">{{ policyDetails.mapfrePolicy?.policyNumber || policyDetails.mapfrePolicy?.contractNumber || 'N/A' }}</span>
                </div>
                <div class="tw-flex tw-justify-between tw-items-center">
                  <span class="tw-text-gray-600">Contract Number:</span>
                  <span class="tw-font-semibold">{{ policyDetails.mapfrePolicy?.contractNumber || 'N/A' }}</span>
                </div>
                <div class="tw-flex tw-justify-between tw-items-center">
                  <span class="tw-text-gray-600">Issue Date:</span>
                  <span class="tw-font-semibold">{{ formatDate(new Date()) }}</span>
                </div>
                <div class="tw-flex tw-justify-between tw-items-center">
                  <span class="tw-text-gray-600">Status:</span>
                  <v-chip color="success" size="small">Active</v-chip>
                </div>
              </div>
            </div>

            <!-- Travel Details -->
            <div>
              <h3 class="tw-text-lg tw-font-semibold tw-mb-4 tw-text-primary">Travel Details</h3>
              <div class="tw-space-y-3">
                <div class="tw-flex tw-justify-between tw-items-center">
                  <span class="tw-text-gray-600">Destination:</span>
                  <span class="tw-font-semibold">{{ policyDetails.quote?.destination || 'N/A' }}</span>
                </div>
                <div class="tw-flex tw-justify-between tw-items-center">
                  <span class="tw-text-gray-600">Travel Dates:</span>
                  <span class="tw-font-semibold">
                    {{ formatDateRange(policyDetails.quote?.start_date, policyDetails.quote?.end_date) }}
                  </span>
                </div>
                <div class="tw-flex tw-justify-between tw-items-center">
                  <span class="tw-text-gray-600">Coverage Amount:</span>
                  <span class="tw-font-semibold">USD {{ policyDetails.quote?.coverage_amount | formatNum }}</span>
                </div>
                <div class="tw-flex tw-justify-between tw-items-center">
                  <span class="tw-text-gray-600">Premium Paid:</span>
                  <span class="tw-font-semibold tw-text-green-600">USD {{ policyDetails.quote?.premium | formatNum }}</span>
                </div>
              </div>
            </div>
          </div>
        </v-card-text>
      </v-card>

      <!-- Coverage Summary -->
      <v-card class="tw-mb-8" elevation="4">
        <v-card-title class="tw-bg-blue-50">
          <v-icon class="tw-mr-3" color="primary">mdi-shield-star</v-icon>
          Coverage Summary
        </v-card-title>

        <v-card-text class="tw-p-6">
          <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-4 tw-gap-4">
            <div class="tw-bg-green-50 tw-p-4 tw-rounded-lg tw-border tw-border-green-200">
              <v-icon color="success" class="tw-mb-2">mdi-medical-bag</v-icon>
              <h4 class="tw-font-semibold tw-text-green-800">Medical Emergency</h4>
              <p class="tw-text-sm tw-text-green-600">Up to USD {{ policyDetails.quote?.coverage_amount | formatNum }}</p>
            </div>

            <div class="tw-bg-blue-50 tw-p-4 tw-rounded-lg tw-border tw-border-blue-200">
              <v-icon color="primary" class="tw-mb-2">mdi-airplane-off</v-icon>
              <h4 class="tw-font-semibold tw-text-blue-800">Trip Cancellation</h4>
              <p class="tw-text-sm tw-text-blue-600">Up to USD {{ (policyDetails.quote?.coverage_amount * 0.8) | formatNum }}</p>
            </div>

            <div class="tw-bg-purple-50 tw-p-4 tw-rounded-lg tw-border tw-border-purple-200">
              <v-icon color="purple" class="tw-mb-2">mdi-briefcase</v-icon>
              <h4 class="tw-font-semibold tw-text-purple-800">Luggage Protection</h4>
              <p class="tw-text-sm tw-text-purple-600">Up to USD {{ (policyDetails.quote?.coverage_amount * 0.1) | formatNum }}</p>
            </div>

            <div class="tw-bg-orange-50 tw-p-4 tw-rounded-lg tw-border tw-border-orange-200">
              <v-icon color="orange" class="tw-mb-2">mdi-helicopter</v-icon>
              <h4 class="tw-font-semibold tw-text-orange-800">Emergency Evacuation</h4>
              <p class="tw-text-sm tw-text-orange-600">Up to USD 1,000,000</p>
            </div>
          </div>
        </v-card-text>
      </v-card>

      <!-- Action Buttons -->
      <v-card elevation="4">
        <v-card-text class="tw-p-6">
          <h3 class="tw-text-lg tw-font-semibold tw-mb-6 tw-text-primary">Next Steps</h3>

          <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-4 tw-gap-4">
            <!-- Download Policy PDF -->
            <v-btn
              color="primary"
              size="large"
              variant="flat"
              @click="downloadPolicyPDF"
              :loading="downloadLoading"
              :disabled="!policyDetails.mapfrePolicy?.fileUrl"
              class="tw-h-16"
            >
              <v-icon start>mdi-download</v-icon>
              <div class="tw-text-center">
                <div class="tw-font-semibold">Download Policy</div>
                <div class="tw-text-xs">PDF Certificate</div>
              </div>
            </v-btn>

            <!-- Email Policy -->
            <v-btn
              color="success"
              size="large"
              variant="flat"
              @click="emailPolicy"
              :loading="emailLoading"
              class="tw-h-16"
            >
              <v-icon start>mdi-email</v-icon>
              <div class="tw-text-center">
                <div class="tw-font-semibold">Email Policy</div>
                <div class="tw-text-xs">Send to inbox</div>
              </div>
            </v-btn>

            <!-- View My Policies -->
            <v-btn
              color="info"
              size="large"
              variant="outlined"
              @click="viewMyPolicies"
              class="tw-h-16"
            >
              <v-icon start>mdi-folder-multiple</v-icon>
              <div class="tw-text-center">
                <div class="tw-font-semibold">My Policies</div>
                <div class="tw-text-xs">View all policies</div>
              </div>
            </v-btn>

            <!-- Get New Quote -->
            <v-btn
              color="secondary"
              size="large"
              variant="outlined"
              @click="getNewQuote"
              class="tw-h-16"
            >
              <v-icon start>mdi-plus-circle</v-icon>
              <div class="tw-text-center">
                <div class="tw-font-semibold">New Quote</div>
                <div class="tw-text-xs">Get another quote</div>
              </div>
            </v-btn>
          </div>

          <!-- Important Notes -->
          <v-alert
            type="info"
            variant="outlined"
            class="tw-mt-6"
          >
            <v-alert-title>Important Notes</v-alert-title>
            <ul class="tw-mt-2 tw-space-y-1">
              <li>• Keep your policy certificate with you while traveling</li>
              <li>• Contact the 24/7 emergency assistance hotline if you need help during your trip</li>
              <li>• Report any claims as soon as possible after the incident</li>
              <li>• Your policy coverage begins on the travel start date specified</li>
            </ul>
          </v-alert>
        </v-card-text>
      </v-card>
    </div>

    <!-- Loading State -->
    <div v-else>
      <v-skeleton-loader
        type="card, card, card"
        class="tw-space-y-6"
      ></v-skeleton-loader>
    </div>

    <!-- Email Dialog -->
    <v-dialog v-model="emailDialog" max-width="500px">
      <v-card>
        <v-card-title>Email Policy Certificate</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="emailAddress"
            label="Email Address"
            type="email"
            variant="outlined"
            prepend-inner-icon="mdi-email"
            :rules="[required('Email'), emailFormat()]"
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="emailDialog = false">Cancel</v-btn>
          <v-btn color="primary" @click="sendEmailConfirmation" :loading="emailLoading">
            Send Email
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import helpers from '@/utils/helpers';
import validations from '@/utils/validations';

export default {
  name: 'PolicyResult',
  mixins: [validations],
  data() {
    return {
      loading: true,
      downloadLoading: false,
      emailLoading: false,
      emailDialog: false,
      emailAddress: '',
      policyDetails: {
        localPolicy: null,
        mapfrePolicy: null,
        quote: null,
      },
    };
  },
  computed: {
    ...mapGetters('rfq', ['getTravelPolicyResult']),
    ...mapGetters('auth', ['authenticatedUser', 'isAuthenticated']),
  },
  filters: {
    formatNum: helpers.formatNum,
  },
  async mounted() {
    await this.loadPolicyDetails();
    this.loading = false;
  },
  methods: {
    async loadPolicyDetails() {
      try {
        // Get policy details from Vuex store first
        const storeDetails = this.getTravelPolicyResult;
        if (storeDetails && storeDetails.mapfrePolicy) {
          this.policyDetails = storeDetails;
          return;
        }

        // If not in store, fetch from API using route params
        const { policyId } = this.$route.params;
        if (policyId) {
          const response = await this.$axios.get(`customer/digital-quotation/pacis-travel/policy/${policyId}`);
          this.policyDetails = {
            localPolicy: response.data,
            mapfrePolicy: {
              policyNumber: response.data.mapfre_policy_number,
              contractNumber: response.data.mapfre_contract_number,
              guid: response.data.mapfre_guid,
              fileUrl: response.data.mapfre_file_url,
            },
            quote: response.data.quote || {},
          };
        }
      } catch (error) {
        // console.error('Failed to load policy details:', error);
        this.$toast.error('Failed to load policy details', 'Error');
      }
    },

    async downloadPolicyPDF() {
      if (!this.policyDetails.mapfrePolicy?.fileUrl) {
        this.$toast.error('Policy PDF not available', 'Error');
        return;
      }

      this.downloadLoading = true;
      try {
        // Create a temporary anchor element to download the file
        const link = document.createElement('a');
        link.href = this.policyDetails.mapfrePolicy.fileUrl;
        link.download = `travel-policy-${this.policyDetails.mapfrePolicy.policyNumber || 'certificate'}.pdf`;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        this.$toast.success('Policy PDF download started', 'Success');
      } catch (error) {
        // console.error('Download failed:', error);
        this.$toast.error('Failed to download policy PDF', 'Error');
      } finally {
        this.downloadLoading = false;
      }
    },

    emailPolicy() {
      this.emailAddress = this.policyDetails.quote?.email_address
                         || this.authenticatedUser?.email || '';
      this.emailDialog = true;
    },

    async sendEmailConfirmation() {
      if (!this.emailAddress || !this.emailFormat()(this.emailAddress)) {
        this.$toast.error('Please enter a valid email address', 'Error');
        return;
      }

      this.emailLoading = true;
      try {
        await this.$axios.post('customer/digital-quotation/pacis-travel/policy/email', {
          policy_id: this.policyDetails.localPolicy?.id,
          email: this.emailAddress,
          pdf_url: this.policyDetails.mapfrePolicy?.fileUrl,
        });

        this.$toast.success(`Policy certificate sent to ${this.emailAddress}`, 'Success');
        this.emailDialog = false;
      } catch (error) {
        // console.error('Email failed:', error);
        this.$toast.error('Failed to send policy email', 'Error');
      } finally {
        this.emailLoading = false;
      }
    },

    viewMyPolicies() {
      this.$router.push({ name: 'MyPolicies' });
    },

    getNewQuote() {
      this.$router.push({ name: 'Request' });
    },

    formatDate(date) {
      if (!date) return 'N/A';
      try {
        return new Date(date).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        });
      } catch (error) {
        return 'N/A';
      }
    },

    formatDateRange(startDate, endDate) {
      if (!startDate || !endDate) return 'N/A';
      try {
        const start = new Date(startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        const end = new Date(endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        return `${start} - ${end}`;
      } catch (error) {
        return `${startDate} - ${endDate}`;
      }
    },
  },
};
</script>

<style scoped>
.tw-bg-gradient-to-r {
  background: linear-gradient(to right, var(--v-primary-base), var(--v-primary-darken1));
}
</style>
